import { useState } from 'react';
import styled from 'styled-components';
import { push } from 'redux-first-history';
import { useDispatch } from 'react-redux';
import { spacing, colors } from 'ui/theme';
import { PageHeader } from 'ui/pageHeader';
import { Delete } from 'ui/icons';
import { Banner } from 'ui/banner';
import { BannerVariants } from 'ui/banner/types';
import { DeleteItemsModal } from 'ui/indexPages/modals/DeleteItemsModal';
import { WorkflowWebhookOverflowMenu } from '../../workflowWebhooksIndex/components/WorkflowWebhookOverflowMenu';
import { CopyWorkflowWebhookModal } from '../../workflowWebhooksIndex/components/CopyWorkflowWebhookModal';
import { deleteWorkflowWebhookRequest } from '../actions';

import { WorkflowWebhook } from '../types';

const displayTextForDeletingWorkflowWebhoks = {
  actionName: {
    verb: 'delete',
    adjective: 'deleted',
  },
  itemTypeName: {
    singular: 'webhook',
    plural: 'webhooks',
  },
  suffixText: 'This cannot be undone.',
  actionIcon: Delete,
};

const BREADCRUMB = {
  href: '/workflows/webhooks',
  content: 'See all journey webhooks',
};

const FullWidthWrapper = styled.div`
  background-color: ${colors.white};
  padding-bottom: ${spacing.base};
`;

const OverflowMenuWrapper = styled.div`
  margin-top: ${spacing.double};
`;

export const WorkflowWebhookHeader = ({
  title,
  workflowWebhook,
}: {
  title?: string;
  workflowWebhook: WorkflowWebhook;
}) => {
  const dispatch = useDispatch();
  const [[showCopyModal, workflowWebhookToCopy], setShowCopyModal] = useState<
    [boolean, WorkflowWebhook | null]
  >([false, null]);
  const [[showDeleteModal, workflowWebhooksToDelete], setShowDeleteModal] = useState<
    [boolean, WorkflowWebhook[]]
  >([false, []]);

  const handleConfirmDelete = () => {
    dispatch(
      deleteWorkflowWebhookRequest(workflowWebhook, () => {
        dispatch(push(`/workflows/webhooks`));
      }),
    );
  };

  const affectedWorkflowNodeIds = new Set();
  const affectedWorkflowIds = new Set();

  workflowWebhooksToDelete.forEach((w) => {
    w.workflowNodes?.forEach((n) => {
      affectedWorkflowNodeIds.add(n.id);
      affectedWorkflowIds.add(n.workflow.id);
    });
  });

  return (
    <FullWidthWrapper>
      <PageHeader
        documentTitle={`${title || 'Create Journey Webhook'} | Journey Webhooks`}
        breadcrumb={BREADCRUMB}
        pageActions={
          workflowWebhook.id ? (
            <OverflowMenuWrapper>
              <WorkflowWebhookOverflowMenu
                workflowWebhook={workflowWebhook}
                showCopyModal={() => setShowCopyModal([true, workflowWebhook])}
                showDeleteModal={() => {
                  setShowDeleteModal([true, [workflowWebhook]]);
                }}
              />
            </OverflowMenuWrapper>
          ) : null
        }
        data-test="create-workflow-webhook-header"
      >
        <span>{title || 'Create Journey Webhook'}</span>
      </PageHeader>
      {showCopyModal && (
        <CopyWorkflowWebhookModal
          workflowWebhook={workflowWebhookToCopy}
          onClose={() => setShowCopyModal([false, null])}
          refetch={() => null}
        />
      )}
      {showDeleteModal && (
        <DeleteItemsModal
          onConfirm={handleConfirmDelete}
          onClose={() => {
            if (workflowWebhooksToDelete.length > 0) {
              setShowDeleteModal([false, []]);
            }
          }}
          /* @ts-expect-error */
          content={workflowWebhooksToDelete}
          displayTextForContentItems={displayTextForDeletingWorkflowWebhoks}
          warning={
            affectedWorkflowNodeIds.size > 0 ? (
              <Banner variant={BannerVariants.WARNING}>
                <b>{affectedWorkflowNodeIds.size}</b> journey tile
                {affectedWorkflowNodeIds.size === 1 ? '' : 's'} (in{' '}
                <b>{affectedWorkflowIds.size}</b> journey
                {affectedWorkflowIds.size === 1 ? '' : 's'}) will no longer be able to call this
                webhook. Consider updating your journey{affectedWorkflowIds.size === 1 ? '' : 's'}{' '}
                before deleting the webhook.
              </Banner>
            ) : null
          }
        />
      )}
    </FullWidthWrapper>
  );
};
